import React from 'react'
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react'
import { Button } from './Button'

interface ErrorPageProps {
 error?: Error | null
 errorInfo?: React.ErrorInfo | null
 onReset?: () => void
 title?: string
 message?: string
 showDetails?: boolean
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
 error,
 errorInfo,
 onReset,
 title,
 message,
 showDetails = false
}) => {
 const getErrorMessage = () => {
 if (message) return message
 if (error?.message) return error.message
 
 // Verifica se é erro de API
 if (error?.name === 'ApiError' || error?.message?.includes('fetch') || error?.message?.includes('Network')) {
 return 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.'
 }
 
 return 'Ocorreu um erro inesperado. Por favor, tente novamente.'
 }

 const getErrorTitle = () => {
 if (title) return title
 
 if (error?.name === 'ApiError' || error?.message?.includes('fetch') || error?.message?.includes('Network')) {
 return 'Erro de Conexão'
 }
 
 return 'Ops! Algo deu errado'
 }

 const handleGoHome = () => {
 // Usa window.location diretamente para não depender do Router
 window.location.href = '/'
 if (onReset) {
 onReset()
 }
 }

 const handleReload = () => {
 if (onReset) {
 onReset()
 }
 window.location.reload()
 }

 return (
 <div className="min-h-screen flex items-center justify-center bg-surface px-4 py-12">
 <div className="max-w-2xl w-full">
 <div className="bg-card rounded-lg shadow-lg p-8 text-center">
 {/* Ícone de Erro */}
 <div className="flex justify-center mb-6">
 <div className="rounded-full bg-red-100 dark:bg-red-900/20 p-4">
 <AlertTriangle className="h-12 w-12 text-red-600 dark:text-red-400" />
 </div>
 </div>

 {/* Título */}
 <h1 className="text-3xl font-bold text-foreground mb-4">
 {getErrorTitle()}
 </h1>

 {/* Mensagem */}
 <p className="text-lg text-muted-foreground mb-8">
 {getErrorMessage()}
 </p>

 {/* Ações */}
 <div className="flex flex-col sm:flex-row gap-4 justify-center">
 <Button
 onClick={handleReload}
 className="flex items-center justify-center gap-2"
 >
 <RefreshCw className="h-4 w-4" />
 Recarregar Página
 </Button>
 
 <Button
 variant="outline"
 onClick={handleGoHome}
 className="flex items-center justify-center gap-2"
 >
 <Home className="h-4 w-4" />
 Ir para Início
 </Button>
 </div>

 {/* Detalhes do Erro (apenas em desenvolvimento ou se showDetails for true) */}
 {(showDetails || import.meta.env.DEV) && error && (
 <details className="mt-8 text-left">
 <summary className="cursor-pointer text-sm font-medium text-foreground mb-2 flex items-center gap-2">
 <Bug className="h-4 w-4" />
 Detalhes do Erro (para desenvolvedores)
 </summary>
 <div className="mt-4 p-4 bg-surface rounded-lg overflow-auto max-h-64">
 <div className="text-xs font-mono text-foreground">
 <div className="mb-2">
 <strong>Erro:</strong> {error.name}
 </div>
 <div className="mb-2">
 <strong>Mensagem:</strong> {error.message}
 </div>
 {error.stack && (
 <div className="mb-2">
 <strong>Stack:</strong>
 <pre className="mt-1 whitespace-pre-wrap text-xs">
 {error.stack}
 </pre>
 </div>
 )}
 {errorInfo && errorInfo.componentStack && (
 <div>
 <strong>Component Stack:</strong>
 <pre className="mt-1 whitespace-pre-wrap text-xs">
 {errorInfo.componentStack}
 </pre>
 </div>
 )}
 </div>
 </div>
 </details>
 )}

 {/* Informações adicionais */}
 <div className="mt-8 pt-6 border-t border-border">
 <p className="text-sm text-muted-foreground">
 Se o problema persistir, entre em contato com o suporte técnico.
 </p>
 </div>
 </div>
 </div>
 </div>
 )
}

